import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Setup } from '../../setup/shared/service/setup';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PICKLIST_GROUPS } from '../../../core/config/picklist.config';
import { take } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { OnboradingService } from '../shared/service/onboarding.service';
import { BackButton } from '../../shared/buttons/back-button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { blockManualDateEntry as blockManualDateEntryUtil } from '../../../core/shared/dynamic-form/date-input.utils';

@Component({
  selector: 'app-risk-fatca',
  imports: [CommonModule, FormsModule, BackButton, TranslatePipe],
  templateUrl: './risk-fatca.html',
})
export class RiskFatca implements OnInit {
  questionnaireSeq = 3;
  questionTypeMap: Record<number, string> = {};
  questions: any[] = [];
  submitted = false;

  crprtOnbrdngSeq: number | null = null;
  clntSeq: number | null = null;
  isViewMode = false;

  constructor(
    private setup: Setup,
    private picklist: PickListService,
    private onboarding: OnboradingService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private router: Router,
    private translate: TranslateService,
  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) this.crprtOnbrdngSeq = +id;
    });

    this.route.queryParamMap.subscribe((params) => {
      const clientSeq = params.get('clientSeq');
      if (clientSeq) this.clntSeq = +clientSeq;
      this.isViewMode = params.get('mode') === 'view';
    });

    this.picklist.loadCodes([PICKLIST_GROUPS.QUESTION_TYPE]);

    this.picklist.picklists$.pipe(take(1)).subscribe((res: any) => {
      const list = res?.[PICKLIST_GROUPS.QUESTION_TYPE] || [];
      const map: Record<number, string> = {};
      list.forEach((item: any) => {
        map[Number(item.refCdSeq)] = item.refCdDscr;
      });
      this.questionTypeMap = map;
      this.loadQuestionsWithAnswers();
    });
  }

  // ================= TYPE MAPPING =================
  // Question type (radio / checkbox / select / dropdown / text) is fully
  // driven by the picklist label coming from the API — nothing is hardcoded.
  getInputType(qstTypKey: number): string {
    const label = this.questionTypeMap[qstTypKey]?.toLowerCase() || '';
    if (label.includes('radio')) return 'radio';
    if (label.includes('check')) return 'checkbox';
    if (label.includes('select') || label.includes('drop')) return 'select';
    return 'text';
  }

  getQuestionTypeLabel(type: number): string {
    return this.questionTypeMap[type] || 'Unknown';
  }

  // ================= DATE QUESTIONS =================
  /** The backend's regex for an ISO date answer, e.g. the "As of date" question. */
  private readonly ISO_DATE_EXPRESSION = String.raw`^\d{4}-\d{2}-\d{2}$`;

  /**
   * A text question configured with the ISO date validation expression (e.g.
   * "As of date") is rendered as a calendar field. The signal is the backend's
   * own `validationExpression`, so no question text or seq is hardcoded.
   */
  isDateQuestion(q: any): boolean {
    if (q?.inputType !== 'text') {
      return false;
    }
    return String(q?.validationExpression ?? '').trim() === this.ISO_DATE_EXPRESSION;
  }

  /** Calendar picker only — typing and arrow-key edits are blocked. */
  readonly blockManualDateEntry = blockManualDateEntryUtil;

  // ================= INPUT LENGTH LIMITS =================
  /**
   * Caps for questions whose `validationExpression` carries no length of its
   * own (the numeric ones are plain `^(0|[1-9]\d*)$`), keyed by `qstSeq`:
   * 44/45 are the director and key-management counts.
   * Anything not listed falls back to the length inside its expression.
   */
  private readonly MAX_LENGTH_BY_QUESTION: Record<number, number> = {
    44: 2,
    45: 2,
  };

  /**
   * Front-end rules that replace the backend `validationExpression`, keyed by `qstSeq`.
   * Expected modes of transactions: digits only, at most 2.
   */
  private readonly VALIDATION_EXPRESSION_OVERRIDE_BY_QUESTION: Record<number, string> = {
    13: '^[0-9]{1,2}$',
  };

  /**
   * Expected annual income, expected monthly turnover and net equity / net
   * assets: a Relationship Officer (LNLY_RO) may enter any positive amount,
   * so the backend's length cap is dropped for them. Other roles keep it.
   */
  private readonly UNCAPPED_AMOUNT_QUESTIONS = new Set([19, 20, 21]);
  private readonly isRelationshipOfficer =
    (localStorage.getItem('user_role') || '').trim().toUpperCase() === 'LNLY_RO';

  private isUncappedAmount(seq: any): boolean {
    return this.isRelationshipOfficer && this.UNCAPPED_AMOUNT_QUESTIONS.has(Number(seq));
  }

  // ================= AMOUNT FORMATTING =================
  /**
   * The same three PKR amounts are shown with thousands separators.
   * Only the display carries commas — `q.value` stays plain
   * digits, so validation and the saved payload are unchanged.
   */
  isAmountQuestion(q: any): boolean {
    return this.UNCAPPED_AMOUNT_QUESTIONS.has(Number(q?.questionId));
  }

  formatAmount(value: any): string {
    const digits = String(value ?? '').replace(/\D/g, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /** Keeps `q.value` as digits and re-inserts the commas without moving the caret. */
  onAmountInput(event: Event, q: any): void {
    const input = event.target as HTMLInputElement;
    const caret = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = input.value.slice(0, caret).replace(/\D/g, '').length;

    let digits = input.value.replace(/\D/g, '');
    // The cap counts digits, so it can't sit on the input as `maxlength`
    // once commas are part of the shown text.
    const maxLength = this.getMaxLength(q);
    if (maxLength) {
      digits = digits.slice(0, maxLength);
    }

    q.value = digits;
    const formatted = this.formatAmount(digits);
    input.value = formatted;

    let position = 0;
    for (let seen = 0; position < formatted.length && seen < digitsBeforeCaret; position++) {
      if (/\d/.test(formatted[position])) {
        seen++;
      }
    }
    input.setSelectionRange(position, position);
  }

  // ================= DEPENDENT QUESTIONS =================
  /**
   * Questions shown only for a particular answer of another question, keyed by `qstSeq`:
   * 48 (GIIN) is shown only when 47 (PPFI under FATCA) is answered "No".
   */
  private readonly DEPENDENT_QUESTIONS: Record<number, { parentSeq: number; showOn: string }> = {
    48: { parentSeq: 47, showOn: 'yes' },
  };

  isQuestionVisible(q: any): boolean {
    const rule = this.DEPENDENT_QUESTIONS[Number(q?.questionId)];
    if (!rule) {
      return true;
    }
    const parent = this.questions.find((item) => Number(item.questionId) === rule.parentSeq);
    return (parent?.value ?? '').toString().trim().toLowerCase() === rule.showOn;
  }

  /** Clears the answer of any dependent question that the new answer hides. */
  private clearHiddenDependents(parent: any): void {
    this.questions.forEach((q) => {
      const rule = this.DEPENDENT_QUESTIONS[Number(q.questionId)];
      if (rule?.parentSeq === Number(parent?.questionId) && !this.isQuestionVisible(q)) {
        q.value = null;
      }
    });
  }

  onAnswerChange(q: any): void {
    this.onSpecifyTriggerChange(q);
    this.clearHiddenDependents(q);
  }

  /**
   * Stops the answer growing past what the backend accepts. The limit comes
   * from the question's own expression where it states one — `^[A-Za-z0-9]{24}$`
   * (IBAN) caps at 24 — so a changed rule needs no change here.
   */
  getMaxLength(q: any): number | null {
    if (this.isUncappedAmount(q?.questionId)) {
      return null;
    }
    const override = this.MAX_LENGTH_BY_QUESTION[Number(q?.questionId)];
    if (override) {
      return override;
    }
    // A date answer is picked from the calendar, so it needs no cap.
    if (this.isDateQuestion(q)) {
      return null;
    }
    const expression = String(q?.validationExpression ?? '').trim();
    // Takes the upper bound of a trailing quantifier: `{24}` or `{1,24}`.
    const quantifier = expression.match(/\{(\d+)(?:,(\d+))?\}\$?$/);
    const upperBound = quantifier ? Number(quantifier[2] ?? quantifier[1]) : NaN;
    if (Number.isFinite(upperBound) && upperBound > 0) {
      return upperBound;
    }
    // Free text (e.g. "Purpose & intended nature of business relationship")
    // has no rule of its own, but every answer lands in the same `ans_vl`
    // column, so it is capped at what that column holds.
    return q?.inputType === 'text' && !this.isAmountQuestion(q) ? this.ANSWER_MAX_LENGTH : null;
  }

  /** Size of the backend `rsk_fcta_onbrdng.ans_vl` column every answer is saved in. */
  readonly ANSWER_MAX_LENGTH = 255;

  /** Free-text answers show a live "n / 255" count under the input. */
  showsCharacterCount(q: any): boolean {
    return this.getMaxLength(q) === this.ANSWER_MAX_LENGTH;
  }

  answerLength(q: any): number {
    return String(q?.value ?? '').length;
  }

  /** Catches a value longer than the cap that `maxlength` didn't stop (e.g. loaded from an older save). */
  private isTooLong(q: any): boolean {
    const maxLength = this.getMaxLength(q);
    return !!maxLength && q?.inputType === 'text' && this.answerLength(q) > maxLength;
  }

  // ================= ALLOWED CHARACTERS =================
  /** A plain whole-number expression, named in the format error message. */
  private readonly NUMERIC_EXPRESSION = String.raw`^(0|[1-9]\d*)$`;
  private readonly DIGIT = /^[0-9]$/;
  private readonly LETTER_OR_DIGIT = /^[A-Za-z0-9]$/;
  private readonly LETTER_DIGIT_OR_SPACE = /^[A-Za-z0-9 ]$/;

  /**
   * The single character each typed answer may contain, keyed by `qstSeq` —
   * the seq is fixed while the question text can be renamed in setup.
   * Bank Name keeps spaces ("Meezan Bank"), IBAN doesn't.
   */
  private readonly ALLOWED_CHARACTER_BY_QUESTION: Record<number, RegExp> = {
    13: this.DIGIT, // Expected modes of transactions / delivery channels
    17: this.LETTER_DIGIT_OR_SPACE, // Bank Name
    18: this.LETTER_OR_DIGIT, // IBAN
    19: this.DIGIT, // Expected annual income (PKR)
    20: this.DIGIT, // Expected monthly turnover (PKR)
    21: this.DIGIT, // Net equity / net assets (PKR as of)
    44: this.DIGIT, // Total Count of Directors
    45: this.DIGIT, // Total Count of Key Management
    48: this.LETTER_OR_DIGIT, // GIIN
  };

  /** The single character a typed answer may contain, or null for no filter. */
  private getAllowedCharacter(q: any): RegExp | null {
    if (q?.inputType !== 'text' || this.isDateQuestion(q)) {
      return null;
    }
    return this.ALLOWED_CHARACTER_BY_QUESTION[Number(q?.questionId)] ?? null;
  }

  /** Stops a disallowed key from reaching the input; shortcuts and editing keys pass. */
  blockDisallowedKey(event: KeyboardEvent, q: any): void {
    const allowed = this.getAllowedCharacter(q);
    if (!allowed || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length === 1 && !allowed.test(event.key)) {
      event.preventDefault();
    }
  }

  /** Strips what the key filter can't see: pasted, dropped or autofilled text. */
  stripDisallowedCharacters(event: Event, q: any): void {
    const allowed = this.getAllowedCharacter(q);
    if (!allowed) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const cleaned = [...input.value].filter((ch) => allowed.test(ch)).join('');
    if (cleaned !== input.value) {
      input.value = cleaned;
      q.value = cleaned;
    }
  }

  // ================= "SPECIFY" FIELDS =================
  // Some questions carry an extra "Please Specify" text field that only
  // enables once a particular answer is picked:
  //  - "Is there any foreign funding in your entity?" -> enabled on "Yes"
  //  - "Shareholder Category" -> enabled on "Other"
  //  - "...claims any other FATCA status" -> enabled on "Other"
  isForeignFundingQuestion(q: any): boolean {
    return (q.question || '').toLowerCase().includes('foreign funding');
  }

  isShareholderCategoryQuestion(q: any): boolean {
    return (q.question || '').toLowerCase().includes('shareholder categ');
  }

  isFatcaStatusQuestion(q: any): boolean {
    return (q.question || '').toLowerCase().includes('fatca status');
  }

  hasSpecifyField(q: any): boolean {
    return (
      this.isForeignFundingQuestion(q) ||
      this.isShareholderCategoryQuestion(q) ||
      this.isFatcaStatusQuestion(q)
    );
  }

  isYes(q: any): boolean {
    return (q.value || '').toString().toLowerCase() === 'yes';
  }

  isOtherSelected(q: any): boolean {
    return (q.value || '').toString().trim().toLowerCase().startsWith('other');
  }

  isSpecifyTriggered(q: any): boolean {
    if (this.isForeignFundingQuestion(q)) return this.isYes(q);
    if (this.isShareholderCategoryQuestion(q) || this.isFatcaStatusQuestion(q)) {
      return this.isOtherSelected(q);
    }
    return false;
  }

  onSpecifyTriggerChange(q: any): void {
    if (this.hasSpecifyField(q) && !this.isSpecifyTriggered(q)) {
      q.specify = '';
    }
  }

  isSpecifyInvalid(q: any): boolean {
    if (!this.submitted) return false;
    if (!this.isSpecifyTriggered(q)) return false;
    return this.isEmpty(q.specify);
  }

  // ================= TOGGLE CHECKBOX =================
  toggleCheckbox(q: any, value: string): void {
    if (!q.value) q.value = [];
    const index = q.value.indexOf(value);
    if (index > -1) {
      q.value.splice(index, 1);
    } else {
      q.value.push(value);
    }
  }

  // ================= LOAD QUESTIONS =================
  loadQuestionsWithAnswers(): void {
    this.setup.getActQuestionList(this.questionnaireSeq).subscribe({
      next: (questions: any[]) => {
        if (!questions?.length) {
          this.questions = [];
          return;
        }

        const answerCalls = questions.map((q) => this.setup.getAnswerList(q.qstSeq));

        forkJoin(answerCalls).subscribe({
          next: (answersArray: any[][]) => {
            this.questions = questions.map((q, index) => ({
              questionId: q.qstSeq,
              question: q.qstStr,
              type: q.qstTypKey,
              typeLabel: this.getQuestionTypeLabel(q.qstTypKey),
              inputType: this.getInputType(q.qstTypKey),
              validationExpression:
                (this.isUncappedAmount(q.qstSeq) ? this.NUMERIC_EXPRESSION : undefined) ??
                this.VALIDATION_EXPRESSION_OVERRIDE_BY_QUESTION[Number(q.qstSeq)] ??
                q.validationExpression ??
                null,
              category: q.qstCtgryKey,
              value: null,
              specify: '',
              answers: (answersArray[index] || []).map((a: any) => ({
                answerId: a.answrSeq,
                answer: a.answrStr,
              })),
            }));

            if (this.crprtOnbrdngSeq) {
              this.loadExistingAnswers();
            }
          },
        });
      },
    });
  }

  // ================= LOAD EXISTING =================
  loadExistingAnswers(): void {
    if (!this.crprtOnbrdngSeq) return;

    this.onboarding.getRiskFatca(this.crprtOnbrdngSeq).subscribe({
      next: (res: any) => {
        const data = res?.qstnWthAns || [];
        if (!data.length) return;

        data.forEach((saved: any) => {
          const q = this.questions.find((q) => q.questionId === saved.qstnSq);
          if (!q) return;

          if (this.hasSpecifyField(q)) {
            q.specify = saved.spcfy ?? '';
          }

          const values = saved.ansVl || [];

          if (q.inputType === 'text') {
            q.value = values[0] ?? '';
          } else if (q.inputType === 'checkbox') {
            q.value = values.map((id: string) => {
              const found = q.answers.find((a: any) => String(a.answerId) === String(id));
              return found ? found.answer : id;
            });
          } else {
            const id = values[0];
            const found = q.answers.find((a: any) => String(a.answerId) === String(id));
            q.value = found ? found.answer : id;
          }
        });
      },
      error: () => {},
    });
  }

  // ================= VALIDATION HELPERS =================
  private isEmpty(value: any): boolean {
    return value === null || value === undefined || value.toString().trim() === '';
  }

  // ================= BACKEND-DRIVEN FORMAT RULES =================
  /**
   * Each question carries its own `validationExpression` (a regex) from the
   * backend — the very rule save-risk-facta rejects on ("Invalid value '...'
   * for question: IBAN"). Applying it here catches the bad value before the
   * save, and stays correct when the backend changes a rule, since nothing
   * about the format is hardcoded.
   *
   * Compiled expressions are cached: this runs on every change-detection pass.
   */
  private readonly compiledExpressions = new Map<string, RegExp | null>();
  private getValidationRegex(expression: string): RegExp | null {
    if (!this.compiledExpressions.has(expression)) {
      try {
        this.compiledExpressions.set(expression, new RegExp(expression));
      } catch {
        // An expression the browser can't compile is treated as "no rule"
        // rather than failing every value against it.
        this.compiledExpressions.set(expression, null);
      }
    }
    return this.compiledExpressions.get(expression) ?? null;
  }

  /**
   * Only typed answers are format-checked — radio / checkbox / dropdown values
   * come from the configured answer list, so they can't be mistyped.
   */
  private failsValidationExpression(q: any): boolean {
    if (q?.inputType !== 'text' || this.isEmpty(q?.value)) {
      return false;
    }
    const expression = String(q?.validationExpression ?? '').trim();
    if (!expression) {
      return false;
    }
    const regex = this.getValidationRegex(expression);
    return regex ? !regex.test(String(q.value).trim()) : false;
  }

  // ================= INVALID CHECK =================
  isInvalid(q: any): boolean {
    if (!this.submitted || !this.isQuestionVisible(q)) return false;
    if (q.inputType === 'checkbox') {
      return !Array.isArray(q.value) || q.value.length === 0;
    }
    return (
      this.isEmpty(q.value) ||
      this.isTooLong(q) ||
      this.failsValidationExpression(q) ||
      this.isBelowMinimumAmount(q)
    );
  }

  /** The three PKR amounts must be at least 100,000 — 0 and anything up to 99,999 is rejected. */
  private readonly MINIMUM_AMOUNT = 100000;
  private isBelowMinimumAmount(q: any): boolean {
    if (!this.isAmountQuestion(q) || this.isEmpty(q?.value)) {
      return false;
    }
    const amount = Number(String(q.value).replace(/,/g, ''));
    return !Number.isFinite(amount) || amount < this.MINIMUM_AMOUNT;
  }

  // ================= ERROR MESSAGES =================
  getErrorMessage(q: any): string {
    if (!this.submitted) return '';
    if (q.inputType === 'checkbox') {
      if (!Array.isArray(q.value) || q.value.length === 0) {
        return this.translate.instant('ONB_KYC.SELECT_AT_LEAST_ONE');
      }
      return '';
    }
    if (this.isEmpty(q.value)) {
      return this.translate.instant('ONB_KYC.FIELD_REQUIRED', { field: q.question });
    }
    if (this.isTooLong(q)) {
      return this.translate.instant('ONB_KYC.MAX_CHARACTERS', {
        field: q.question,
        max: this.getMaxLength(q),
      });
    }
    if (this.failsValidationExpression(q)) {
      return this.getFormatErrorMessage(q);
    }
    if (this.isBelowMinimumAmount(q)) {
      return this.translate.instant('ONB_KYC.MINIMUM_AMOUNT', {
        field: q.question,
        min: this.formatAmount(this.MINIMUM_AMOUNT),
      });
    }
    return '';
  }

  /**
   * The expressions in use are plain enough to name; anything else falls back
   * to a generic message rather than showing the raw regex to the user.
   */
  private getFormatErrorMessage(q: any): string {
    const expression = String(q?.validationExpression ?? '').trim();
    if (expression === this.NUMERIC_EXPRESSION) {
      return this.translate.instant('ONB_KYC.MUST_BE_DIGITS', { field: q.question });
    }
    if (expression === this.ISO_DATE_EXPRESSION) {
      return this.translate.instant('ONB_KYC.MUST_BE_VALID_DATE', { field: q.question });
    }
    const exactLength = expression.match(/^\^\[A-Za-z0-9\]\{(\d+)\}\$$/);
    if (exactLength) {
      return this.translate.instant('ONB_KYC.MUST_BE_EXACTLY_DIGITS', { field: q.question, count: exactLength[1] });
    }
    const maxDigits = expression.match(/^\^\[0-9\]\{1,(\d+)\}\$$/);
    if (maxDigits) {
      return this.translate.instant('ONB_KYC.ONLY_DIGITS_MAX', { field: q.question, max: maxDigits[1] });
    }
    const maxLength = expression.match(/^\^\[A-Za-z0-9\]\{1,(\d+)\}\$$/);
    if (maxLength) {
      return this.translate.instant('ONB_KYC.ONLY_ALPHANUMERIC_MAX', { field: q.question, max: maxLength[1] });
    }
    return this.translate.instant('ONB_KYC.INVALID_VALUE_FOR', { field: q.question });
  }

  // ================= MAIN VALIDATE =================
  validate(): boolean {
    this.submitted = true;

    const anyInvalid = this.questions.some((q) => this.isInvalid(q) || this.isSpecifyInvalid(q));

    if (anyInvalid) {
      this.toastr.warning(this.translate.instant('ONB_KYC.FIX_HIGHLIGHTED_FIELDS'));
      return false;
    }

    return true;
  }

  // ================= SAVE =================
  save(): void {
    if (this.isViewMode) return;
    if (!this.validate()) return;

    if (!this.clntSeq || !this.crprtOnbrdngSeq) {
      this.toastr.error(this.translate.instant('ONB_CLIENT_LIST.CLIENT_ID_MISSING'));
      return;
    }

    const payload = this.buildPayload();
    console.log('PAYLOAD', payload);

    this.onboarding.saveRiskFacta(payload).subscribe({
      next: (res: any) => {
        if (res?.__hasError) return;
        this.toastr.success(this.translate.instant('ONBOARDING.SAVED_SUCCESSFULLY'));
        this.submitted = false;
        this.goToUbos();
      },
      error: () => {},
    });
  }

  // ================= BUILD PAYLOAD =================
  buildPayload(): any {
    return {
      clntSeq: this.clntSeq,
      qstnrSeq: this.questionnaireSeq,
      crprtOnbrdngSeq: this.crprtOnbrdngSeq,
      qstnAns: this.questions.map((q) => ({
        qstnSq: q.questionId,
        ansVl: this.getAnswerValue(q),
        spcfy: this.isSpecifyTriggered(q) ? q.specify : null,
      })),
    };
  }

  // ================= GET ANSWER VALUE =================
  getAnswerValue(q: any): string {
    if (!this.isQuestionVisible(q)) {
      return '';
    }

    if (q.inputType === 'text') {
      return String(q.value ?? '');
    }

    if (q.inputType === 'checkbox') {
      const selected = Array.isArray(q.value) ? q.value : [];
      const ids = selected.map((val: string) => {
        const found = q.answers.find((a: any) => a.answer === val);
        return found ? String(found.answerId) : val;
      });
      return ids.join(',');
    }

    const found = q.answers.find((a: any) => a.answer === q.value);
    return found ? String(found.answerId) : String(q.value ?? '');
  }

  // ================= NAV =================
  goToUbos(): void {
    this.router.navigate(['/onboarding/ubos', this.crprtOnbrdngSeq], {
      queryParams: {
        clientSeq: this.clntSeq,
        ...(this.isViewMode ? { mode: 'view' } : {}),
      },
    });
  }
}
